import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import SafeIcon from "../common/SafeIcon";
import * as FiIcons from "react-icons/fi";

const { FiArrowLeft, FiCalendar } = FiIcons;

const ArticleFpaRncp = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <section className="pt-52 pb-20 bg-white min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back link */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <Link
            to="/ressources"
            className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
          >
            <SafeIcon icon={FiArrowLeft} className="h-4 w-4 mr-2" />
            Retour aux ressources
          </Link>
        </motion.div>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-12"
        >
          <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            Le titre FPA RNCP37275 : pas obligatoire aujourd'hui, mais bientôt quasi indispensable
          </h1>
          <div className="flex items-center text-gray-500 text-sm">
            <SafeIcon icon={FiCalendar} className="h-4 w-4 mr-2" />
            <span>Mis en ligne le 13/02/2026</span>
          </div>
        </motion.div>

        {/* Article content */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="prose prose-lg max-w-none"
        >
          <div className="space-y-8">
            <p className="text-lg text-gray-700 leading-relaxed">
              Pendant longtemps, on pouvait "faire de la formation" sans que la question d'un titre reconnu se pose vraiment. Ce temps change. Le Titre professionnel Formateur Professionnel d'Adultes (FPA – RNCP37275) n'est pas une obligation légale pour former des adultes, mais il devient progressivement un standard sur un marché où la qualité et les contrôles se renforcent nettement.
            </p>

            <div className="bg-gray-50 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Non, le RNCP37275 n'est pas une obligation légale
              </h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  À ce jour, aucun texte n'impose de détenir le RNCP37275 pour : animer des actions de formation, créer un organisme de formation, intervenir en sous-traitance ou former en entreprise.
                </p>
                <p>
                  Le droit exige surtout un cadre administratif (déclaration d'activité pour les organismes de formation, obligations de traçabilité, de conformité, etc.), mais il ne conditionne pas l'exercice du métier de formateur à un diplôme unique.
                </p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Alors pourquoi ce titre devient "structurant" ?
              </h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  Parce que le marché n'évolue pas uniquement sous l'effet de la loi : il évolue sous l'effet des financeurs, des appels d'offres, des process qualité, et d'une lutte active contre la fraude.
                </p>
                <p className="font-medium">
                  En clair : ce n'est pas "obligatoire", mais c'est de plus en plus attendu.
                </p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Professionnalisation : le secteur se normalise
              </h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  La formation professionnelle est devenue un domaine très encadré, où l'on attend : une ingénierie pédagogique solide, des évaluations traçables, des pratiques d'accompagnement formalisées, une posture professionnelle conforme à un référentiel métier.
                </p>
                <p>
                  Le titre FPA sert précisément de repère commun : il prouve que le formateur maîtrise les fondamentaux (concevoir, animer, accompagner, évaluer) dans des cadres variés.
                </p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Qualité : Qualiopi a changé le "niveau d'entrée"
              </h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  Depuis le 1er janvier 2022, Qualiopi est la condition pour accéder aux financements publics et mutualisés (OPCO, CPF, etc.). Cette exigence a rehaussé le niveau d'attentes sur : la compétence des intervenants, la cohérence des parcours, la preuve de la réalisation ("service fait"), la capacité à justifier chaque étape.
                </p>
                <p>
                  Même si Qualiopi ne demande pas "le titre FPA", elle pousse tout l'écosystème à exiger des profils certifiés, ou en tout cas facilement "auditables".
                </p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Financeurs : CPF / OPCO / France Travail, une logique de sécurisation
              </h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  Les financeurs cherchent à limiter les risques : formations fictives, sous-traitance opaque, pratiques commerciales agressives, dossiers non conformes. Résultat : ils privilégient de plus en plus les dispositifs et les intervenants dont les compétences sont objectivées.
                </p>
                <p>
                  Le titre FPA devient alors un signal de crédibilité. Pas un sésame automatique, mais un marqueur clair.
                </p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Appels d'offres : la "crédibilité" devient un critère implicite
              </h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  Dans les consultations publiques, les partenariats institutionnels ou les marchés à forte visibilité, la détention d'un titre RNCP cohérent avec la mission (dont le FPA) devient souvent un avantage concurrentiel : plus facile à justifier dans un dossier, plus rassurant pour un acheteur public, plus défendable lors d'un audit.
                </p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Le vrai moteur : l'intensification des contrôles depuis 2023–2025
              </h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  Ce qui change profondément, c'est l'ambiance générale : la formation professionnelle est entrée dans une phase de contrôle renforcé et de tolérance réduite.
                </p>
                <p>Voici les jalons qui marquent cette tendance :</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>
                    <strong>28 décembre 2023 :</strong> décret encadrant notamment le référencement sur "Mon Compte Formation", la sous-traitance et les modalités de contrôle/échanges d'informations pour lutter contre la fraude.
                  </li>
                  <li>
                    <strong>28 mai 2024 (JO 11 juin 2024) :</strong> arrêté portant commissionnement pour effectuer des contrôles au titre de la formation professionnelle.
                  </li>
                  <li>
                    <strong>6 juin 2025 :</strong> décret relatif à la certification professionnelle, qui renforce les pouvoirs de contrôle et les sanctions que peut prononcer France compétences à l'encontre des organismes certificateurs.
                  </li>
                  <li>
                    <strong>Juillet 2025 :</strong> publication d'un plan "qualité et lutte contre la fraude" dans la formation professionnelle (dossier de presse).
                  </li>
                  <li>
                    <strong>Décembre 2025 / Janvier 2026 :</strong> France compétences publie un vademecum actualisé sur RNCP/RS, signe d'un référentiel et d'attendus plus "cadrés" côté certifications.
                  </li>
                </ul>
                <p>
                  Ce contexte a un effet très concret : quand le secteur se durcit, les acteurs (financeurs, partenaires, institutions) recherchent des garanties simples. Le titre FPA est une de ces garanties : lisible, reconnu, standardisé.
                </p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Ce que ça change pour un dirigeant d'organisme de formation (ou un futur formateur)
              </h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  Même si tu peux exercer sans le RNCP37275, ne pas l'avoir peut progressivement te fermer des portes : difficultés à rassurer certains partenaires, moins de poids dans des appels d'offres, plus de friction sur l'accès à certains projets (notamment financés), plus de concurrence à valeur égale (les dossiers "certifiés" passent devant).
                </p>
                <p>
                  À l'inverse, disposer du titre (ou l'obtenir via VAE si l'expérience est déjà là) permet : de sécuriser ton positionnement, d'objectiver tes compétences, de professionnaliser ton discours commercial, de mieux structurer ta conformité (preuves, évaluations, démarche qualité).
                </p>
                <p>
                  Le RNCP37275 n'est pas obligatoire juridiquement, mais il devient un standard professionnel dans un secteur où la qualité est de plus en plus régulée, contrôlée et attendue. Autrement dit : on ne bascule pas vers une "obligation réglementaire", on bascule vers une évolution structurelle du marché.
                </p>
                <p className="font-semibold text-gray-900 bg-blue-50 p-4 rounded-lg mt-4">
                  Pour un formateur, un consultant, un manager formateur interne ou un dirigeant d'OF, s'engager dans ce titre (par formation ou par VAE) n'est pas seulement "un diplôme en plus" : c'est une stratégie de sécurisation et de crédibilité pour les années à venir.
                </p>
              </div>
            </div>
          </div>

          {/* Back to resources */}
          <div className="text-center pt-12">
            <Link
              to="/ressources"
              className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
            >
              <SafeIcon icon={FiArrowLeft} className="h-4 w-4 mr-2" />
              Retour aux ressources
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ArticleFpaRncp;
